import { useEffect, useState } from "react";
import {
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  Power,
  PowerOff,
  RotateCcw,
  Lock,
  Server,
  Shield,
  Unlock,
  Trash2,
  UserCircle,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { useAuth } from "@/auth";
import { passwordRules, validatePassword } from "@/lib/password";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ACTION_COLUMNS,
  MODULES,
  applyCascade,
  emptyAccess,
  missingSupportReadsForMatrix,
  modulePermission,
  normalizeAccess,
  type MissingSupportRead,
  type ModuleActionKey,
  type NestedAccess,
} from "@/lib/permissions";
import { DEFAULT_QUOTATION_TERMS } from "@/lib/quotationTerms";
import { DEFAULT_SALES_INVOICE_TERMS } from "@/lib/salesInvoiceTerms";

interface UserRow {
  id: number;
  username: string;
  email: string;
  name: string;
  roles: string[];
  isActive: boolean;
}

const ALL_ROLES = ["administrator", "manager", "finance", "sales"] as const;
type RoleKey = (typeof ALL_ROLES)[number];

export default function Settings() {
  const { user, can, logout, refreshFeatures, isFeatureEnabled } = useAuth();
  const isAdmin = !!user?.roles?.includes("administrator");
  const canWrite = can("settings.write");
  // Product-level Tax switch. When OFF the Tax tab and the tax-related GL
  // account mappings are hidden — mirrors how the Production Orders flag hides
  // its module everywhere. The flag is still toggled from the Features tab.
  const taxEnabled = isFeatureEnabled("tax");

  // Self-service "My Account" tab state. The form lets any signed-in user
  // change their own password without an admin touching the user table.
  const [myPw, setMyPw] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [myPwShow, setMyPwShow] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [myPwSaving, setMyPwSaving] = useState(false);

  const [settings, setSettings] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<UserRow[]>([]);
  // Tracks the user id whose role is currently being saved, so we can show a
  // disabled/saving state on that row's Select and prevent racing changes.
  const [roleSavingId, setRoleSavingId] = useState<number | null>(null);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingTax, setSavingTax] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [savingInvoiceTerms, setSavingInvoiceTerms] = useState(false);
  const [savingAccounting, setSavingAccounting] = useState(false);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [glAccounts, setGlAccounts] = useState<
    Array<{ id: number; code: string; name: string; type: string }>
  >([]);

  // Add user dialog
  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState<{
    username: string;
    email: string;
    name: string;
    password: string;
    role: RoleKey;
  }>({ username: "", email: "", name: "", password: "", role: "sales" });

  // Reset password dialog
  const [pwOpen, setPwOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState<UserRow | null>(null);
  const [pwValue, setPwValue] = useState("");

  // Module Access dialog
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState<UserRow | null>(null);
  const [accessMatrix, setAccessMatrix] = useState<NestedAccess>(emptyAccess);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessResetting, setAccessResetting] = useState(false);
  // Pending warning shown when the admin's choices would leave the user
  // missing support reads needed to actually use the modules they were
  // granted. Holds the list so the panel can be rendered and so the user can
  // click "Save anyway" without recomputing.
  const [pendingMissing, setPendingMissing] = useState<MissingSupportRead[]>([]);

  type BillingStatus = {
    locked: boolean;
    reason: "manual" | "expired" | null;
    manually_locked: boolean;
    paid_until: string | null;
  };
  type BillingHistoryRow = {
    id: number;
    action: "lock" | "unlock";
    paid_until: string | null;
    note: string | null;
    actor_name: string | null;
    created_at: string;
  };
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [billingHistory, setBillingHistory] = useState<BillingHistoryRow[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLockNote, setBillingLockNote] = useState("");
  const [billingUnlockNote, setBillingUnlockNote] = useState("");
  const [billingUnlockDate, setBillingUnlockDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [billingBusy, setBillingBusy] = useState<"lock" | "unlock" | null>(
    null,
  );

  const setSetting = (k: string, v: string) =>
    setSettings((s) => ({ ...s, [k]: v }));

  async function loadUsers() {
    const u = await api.get<{ users: UserRow[] }>("/users");
    setUsers(u.users);
  }

  async function loadBilling() {
    setBillingLoading(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get<{ billing: BillingStatus }>("/billing/status"),
        api.get<{ history: BillingHistoryRow[] }>("/billing/history"),
      ]);
      setBillingStatus(statusRes.billing);
      setBillingHistory(historyRes.history);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load billing");
    } finally {
      setBillingLoading(false);
    }
  }

  async function lockCompanyAccess() {
    if (
      !window.confirm(
        "Lock the app for all users except administrators? Everyone else will be signed out immediately.",
      )
    ) {
      return;
    }
    setBillingBusy("lock");
    try {
      const res = await api.post<{ billing: BillingStatus }>("/billing/lock", {
        note: billingLockNote.trim() || undefined,
      });
      setBillingStatus(res.billing);
      setBillingLockNote("");
      toast.success("App locked for non-administrators");
      await loadBilling();
    } catch (err) {
      toast.error((err as Error).message || "Lock failed");
    } finally {
      setBillingBusy(null);
    }
  }

  async function unlockCompanyAccess() {
    setBillingBusy("unlock");
    try {
      const res = await api.post<{ billing: BillingStatus }>("/billing/unlock", {
        paidUntil: billingUnlockDate,
        note: billingUnlockNote.trim() || undefined,
      });
      setBillingStatus(res.billing);
      setBillingUnlockNote("");
      toast.success("App unlocked");
      await loadBilling();
    } catch (err) {
      toast.error((err as Error).message || "Unlock failed");
    } finally {
      setBillingBusy(null);
    }
  }

  async function load() {
    try {
      if (can("settings.read")) {
        const s = await api.get<{ settings: Record<string, string> }>("/settings");
        setSettings(s.settings);
      }
      if (isAdmin) {
        await Promise.all([loadUsers(), loadBilling()]);
      }
      if (can("gl.view")) {
        try {
          const a = await api.get<{
            accounts: Array<{ id: number; code: string; name: string; type: string }>;
          }>("/gl/accounts?is_active=1");
          setGlAccounts(a.accounts);
        } catch {
          // gl.view may not be granted yet; ignore.
        }
      }
    } catch (err) {
      toast.error((err as Error).message || "Failed to load settings");
    }
  }

  useEffect(() => {
    load();
  }, [isAdmin]);

  async function saveKeys(
    keys: string[],
    label: string,
    setter: (b: boolean) => void,
  ) {
    setter(true);
    try {
      const payload: Record<string, string> = {};
      for (const k of keys) payload[k] = settings[k] ?? "";
      await api.put("/settings", payload);
      toast.success(`${label} saved`);
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setter(false);
    }
  }

  // Persist a single feature flag (features.*) then refresh the auth context's
  // feature state so the sidebar / dashboard / routes react immediately —
  // without forcing a full sign-out.
  async function saveFeature(key: string, enabled: boolean) {
    setSavingFeatures(true);
    const value = enabled ? "true" : "false";
    setSetting(key, value);
    try {
      await api.put("/settings", { [key]: value });
      await refreshFeatures();
      toast.success(`Features saved`);
    } catch (err) {
      // Roll the local toggle back on failure.
      setSetting(key, enabled ? "false" : "true");
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSavingFeatures(false);
    }
  }

  async function addUser() {
    const uname = userForm.username.trim();
    if (!uname || !userForm.password) {
      return toast.error("Username and password are required");
    }
    if (!/^[a-zA-Z0-9._-]{3,}$/.test(uname)) {
      return toast.error(
        "Username must be 3+ characters and use letters, digits, dot, underscore or hyphen",
      );
    }
    if (!userForm.email.trim()) {
      return toast.error("Email is required");
    }
    if (!userForm.role) {
      return toast.error("Role is required");
    }
    try {
      await api.post("/users", {
        username: uname,
        email: userForm.email.trim(),
        name: (userForm.name || uname).trim(),
        password: userForm.password,
        role: userForm.role,
      });
      toast.success("User created");
      setUserOpen(false);
      setUserForm({
        username: "",
        email: "",
        name: "",
        password: "",
        role: "sales",
      });
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Create failed");
    }
  }

  async function openAccess(target: UserRow) {
    setAccessTarget(target);
    setAccessOpen(true);
    setAccessLoading(true);
    setAccessMatrix(emptyAccess());
    try {
      const res = await api.get<{
        access: NestedAccess;
      }>(`/users/${target.id}/access`);
      setAccessMatrix(normalizeAccess(res.access));
    } catch (err) {
      toast.error((err as Error).message || "Failed to load access");
    } finally {
      setAccessLoading(false);
    }
  }

  async function commitAccess() {
    if (!accessTarget) return;
    setAccessSaving(true);
    try {
      await api.put(`/users/${accessTarget.id}/access`, {
        access: accessMatrix,
      });
      const label =
        accessTarget.name || accessTarget.username || accessTarget.email;
      toast.success(
        `Access updated. ${label} may need to refresh or sign in again.`,
      );
      setPendingMissing([]);
      setAccessOpen(false);
      setAccessTarget(null);
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setAccessSaving(false);
    }
  }

  async function saveAccess() {
    if (!accessTarget) return;
    if (accessTarget.id === user?.id && accessMatrix.settings?.view === false) {
      toast.error("You cannot remove your own Settings access");
      return;
    }
    const missing = missingSupportReadsForMatrix(accessMatrix);
    if (missing.length > 0) {
      setPendingMissing(missing);
      return;
    }
    await commitAccess();
  }

  function toggleCell(moduleKey: string, action: ModuleActionKey) {
    setAccessMatrix((prev) => {
      const current = prev[moduleKey]?.[action] === true;
      // applyCascade still runs the (now minimal) cancel→read mirroring, but
      // it no longer touches Customers / Products when a document module is
      // ticked. The admin's matrix stays exactly what they ticked.
      const next = applyCascade(prev, moduleKey, action, !current);
      return next.matrix;
    });
    if (pendingMissing.length > 0) setPendingMissing([]);
  }

  function handleAccessOpenChange(open: boolean) {
    setAccessOpen(open);
    if (!open) {
      setPendingMissing([]);
      setAccessTarget(null);
    }
  }

  async function resetAccessToRoleDefault() {
    if (!accessTarget) return;
    const label =
      accessTarget.name || accessTarget.username || accessTarget.email;
    if (
      !confirm(
        `Reset Module Access for ${label} to role defaults? Any per-user grants or denies will be cleared.`,
      )
    ) {
      return;
    }
    setAccessResetting(true);
    try {
      await api.del(`/users/${accessTarget.id}/access`);
      const res = await api.get<{ access: NestedAccess }>(
        `/users/${accessTarget.id}/access`,
      );
      setAccessMatrix(normalizeAccess(res.access));
      setPendingMissing([]);
      toast.success(
        `Reset to role defaults. ${label} may need to refresh or sign in again.`,
      );
    } catch (err) {
      toast.error((err as Error).message || "Reset failed");
    } finally {
      setAccessResetting(false);
    }
  }

  function openPwReset(target: UserRow) {
    setPwTarget(target);
    setPwValue("");
    setPwOpen(true);
  }

  async function submitPwReset() {
    if (!pwTarget) return;
    const policy = validatePassword(pwValue);
    if (!policy.ok) {
      return toast.error(policy.message || "Password does not meet the policy.");
    }
    try {
      await api.post(`/users/${pwTarget.id}/password`, { password: pwValue });
      toast.success(
        `Password reset for ${pwTarget.username || pwTarget.email}`,
      );
      setPwOpen(false);
      setPwTarget(null);
      setPwValue("");
    } catch (err) {
      toast.error((err as Error).message || "Reset failed");
    }
  }

  async function submitMyPasswordChange(e: React.FormEvent) {
    e.preventDefault();
    if (!myPw.current || !myPw.next) {
      return toast.error("Current and new password are required");
    }
    const policy = validatePassword(myPw.next);
    if (!policy.ok) {
      return toast.error(policy.message || "Password does not meet the policy.");
    }
    if (myPw.next !== myPw.confirm) {
      return toast.error("New password and confirmation do not match");
    }
    if (myPw.next === myPw.current) {
      return toast.error("New password must be different from the current password");
    }
    setMyPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword: myPw.current,
        newPassword: myPw.next,
      });
      setMyPw({ current: "", next: "", confirm: "" });
      setMyPwShow({ current: false, next: false, confirm: false });
      toast.success("Password updated. You will be signed out shortly.");
      // Force a fresh session so the next sign-in uses the new password.
      setTimeout(() => {
        logout();
      }, 1500);
    } catch (err) {
      const fallback = "Failed to change password";
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; message?: string }
          | null
          | undefined;
        if (body?.error === "current_password_invalid") {
          toast.error("Current password is incorrect");
        } else if (body?.error === "weak_password") {
          toast.error(body.message || "Password does not meet the policy.");
        } else if (body?.error === "same_password") {
          toast.error("New password must be different from the current password");
        } else {
          toast.error(body?.message || err.message || fallback);
        }
      } else {
        toast.error((err as Error).message || fallback);
      }
    } finally {
      setMyPwSaving(false);
    }
  }

  async function toggleActive(target: UserRow) {
    if (target.id === user?.id) {
      return toast.error("You cannot deactivate your own account");
    }
    const nextActive = !target.isActive;
    setUsers((prev) =>
      prev.map((u) =>
        u.id === target.id ? { ...u, isActive: nextActive } : u,
      ),
    );
    try {
      await api.put(`/users/${target.id}`, { isActive: nextActive });
      const label = target.username || target.email;
      toast.success(
        nextActive ? `${label} activated` : `${label} deactivated`,
      );
    } catch (err) {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === target.id ? { ...u, isActive: target.isActive } : u,
        ),
      );
      toast.error((err as Error).message || "Failed to update status");
    }
  }

  async function deleteUser(target: UserRow) {
    if (target.id === user?.id) {
      return toast.error("You cannot delete your own account");
    }
    const label = target.username || target.email;
    if (
      !confirm(
        `Permanently delete ${label}? This cannot be undone. Their name on past records will be kept where it was already saved.`,
      )
    ) {
      return;
    }
    try {
      await api.del(`/users/${target.id}`);
      await loadUsers();
      toast.success(`${label} deleted`);
    } catch (err) {
      toast.error((err as Error).message || "Failed to delete user");
    }
  }

  // Change a user's primary role via the existing access endpoint, which
  // rewrites their user_roles assignment. Per-user permission overrides in
  // user_permissions are intentionally left untouched.
  async function changeRole(target: UserRow, newRole: RoleKey) {
    if (newRole === target.roles?.[0]) return;
    // Guard against self-lockout: never let an admin change their own role.
    if (target.id === user?.id) {
      return toast.error("You cannot change your own role");
    }
    setRoleSavingId(target.id);
    try {
      await api.put(`/users/${target.id}/access`, { roles: [newRole] });
      toast.success(`Role updated to ${newRole}`);
      await loadUsers();
    } catch (err) {
      toast.error((err as Error).message || "Failed to update role");
    } finally {
      setRoleSavingId(null);
    }
  }

  const myAccountSection = (
    <>
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserCircle className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription>
            Your signed-in account. Contact an administrator to change
            your name, username, email, or role.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <ProfileField label="Full name" value={user?.name || "—"} />
          <ProfileField label="Username" value={user?.username || "—"} />
          <ProfileField label="Email" value={user?.email || "—"} />
          <div className="space-y-1.5">
            <Label className="text-muted-foreground">Roles</Label>
            <div className="flex flex-wrap gap-1.5">
              {(user?.roles ?? []).length === 0 ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                (user?.roles ?? []).map((r) => (
                  <Badge
                    key={r}
                    variant="outline"
                    className="capitalize border-primary/30 bg-primary/5 text-primary dark:text-primary"
                  >
                    {r}
                  </Badge>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound className="h-5 w-5" />
            Change Password
          </CardTitle>
          <CardDescription>
            Your new password must meet the policy below. You will be
            signed out after a successful change so the next session uses
            the new password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={submitMyPasswordChange}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <PasswordField
              id="current-password"
              label="Current password"
              autoComplete="current-password"
              value={myPw.current}
              show={myPwShow.current}
              onShowChange={(v) =>
                setMyPwShow((s) => ({ ...s, current: v }))
              }
              onChange={(v) => setMyPw((p) => ({ ...p, current: v }))}
            />
            <div className="hidden md:block" />
            <PasswordField
              id="new-password"
              label="New password"
              autoComplete="new-password"
              value={myPw.next}
              show={myPwShow.next}
              onShowChange={(v) => setMyPwShow((s) => ({ ...s, next: v }))}
              onChange={(v) => setMyPw((p) => ({ ...p, next: v }))}
            />
            <PasswordField
              id="confirm-password"
              label="Confirm new password"
              autoComplete="new-password"
              value={myPw.confirm}
              show={myPwShow.confirm}
              onShowChange={(v) =>
                setMyPwShow((s) => ({ ...s, confirm: v }))
              }
              onChange={(v) => setMyPw((p) => ({ ...p, confirm: v }))}
              error={
                myPw.confirm.length > 0 && myPw.confirm !== myPw.next
                  ? "Does not match the new password"
                  : undefined
              }
            />
            <div className="md:col-span-2">
              <PasswordRules value={myPw.next} />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button
                type="submit"
                className="bg-gradient-primary"
                disabled={
                  myPwSaving ||
                  !myPw.current ||
                  !validatePassword(myPw.next).ok ||
                  myPw.next !== myPw.confirm ||
                  myPw.next === myPw.current
                }
              >
                {myPwSaving ? "Updating..." : "Update password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );

  if (!isAdmin) {
    return (
      <div className="space-y-5 max-w-[1400px]">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            View your profile and update your account password.
          </p>
        </div>
        <div className="space-y-4">{myAccountSection}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Configure your company, tax defaults, terms and team access.
        </p>
      </div>

      <Tabs defaultValue="account" className="space-y-4">
        <TabsList>
          <TabsTrigger value="account">My Account</TabsTrigger>
          <TabsTrigger value="company">Company</TabsTrigger>
          {taxEnabled ? <TabsTrigger value="tax">Tax</TabsTrigger> : null}
          <TabsTrigger value="terms">Terms</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          {can("gl.view") ? (
            <TabsTrigger value="accounting">Accounting</TabsTrigger>
          ) : null}
          <TabsTrigger value="users">User Accounts</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="server">Server</TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="space-y-4">
          {myAccountSection}
        </TabsContent>

        <TabsContent value="company">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Company</CardTitle>
              <CardDescription>
                Used on quotation headers and invoices.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SettingInput
                k="company.name"
                label="Company name"
                settings={settings}
                onChange={setSetting}
              />
              <SettingInput
                k="company.email"
                label="Email"
                settings={settings}
                onChange={setSetting}
              />
              <SettingInput
                k="company.phone"
                label="Phone"
                settings={settings}
                onChange={setSetting}
              />
              <SettingInput
                k="company.address"
                label="Address"
                settings={settings}
                onChange={setSetting}
              />
              <SettingInput
                k="currency.code"
                label="Currency code"
                settings={settings}
                onChange={setSetting}
              />
              <SettingInput
                k="currency.symbol"
                label="Currency symbol"
                settings={settings}
                onChange={setSetting}
              />
              {canWrite ? (
                <div className="md:col-span-2 flex justify-end">
                  <Button
                    onClick={() =>
                      saveKeys(
                        [
                          "company.name",
                          "company.email",
                          "company.phone",
                          "company.address",
                          "currency.code",
                          "currency.symbol",
                        ],
                        "Company settings",
                        setSavingCompany,
                      )
                    }
                    className="bg-gradient-primary"
                    disabled={savingCompany}
                  >
                    {savingCompany ? "Saving..." : "Save company"}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        {taxEnabled ? (
        <TabsContent value="tax">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Tax</CardTitle>
              <CardDescription>
                Defaults applied to new line items.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SettingInput
                k="tax.default_rate"
                label="Default tax rate (%)"
                type="number"
                settings={settings}
                onChange={setSetting}
              />
              {canWrite ? (
                <div className="md:col-span-2 flex justify-end">
                  <Button
                    onClick={() =>
                      saveKeys(["tax.default_rate"], "Tax settings", setSavingTax)
                    }
                    className="bg-gradient-primary"
                    disabled={savingTax}
                  >
                    {savingTax ? "Saving..." : "Save tax"}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
        ) : null}

        <TabsContent value="features">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Features</CardTitle>
              <CardDescription>
                Turn whole modules on or off for this client. Switching a
                feature off hides it everywhere — sidebar, dashboard and direct
                links — for every user, regardless of their permissions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FeatureToggle
                label="Production Orders"
                description="Show the Production module and allow production orders for this client."
                enabled={settings["features.production_orders"] !== "false"}
                disabled={!canWrite || savingFeatures}
                onToggle={(next) =>
                  saveFeature("features.production_orders", next)
                }
              />
              <FeatureToggle
                label="Tax"
                description="Apply VAT / NHIL / GETFund levies on quotations and sales invoices. Turn off to hide all tax inputs, columns, PDF lines and the Tax settings for this client; totals are then computed without tax."
                enabled={settings["features.tax"] !== "false"}
                disabled={!canWrite || savingFeatures}
                onToggle={(next) => saveFeature("features.tax", next)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {can("gl.view") ? (
          <TabsContent value="accounting">
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="text-lg">Accounting Mappings</CardTitle>
                <CardDescription>
                  Default General Ledger accounts used by auto-posting integrations
                  (sales invoices, receipts). Values are account codes.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { k: "gl.account.cash", label: "Cash account" },
                  { k: "gl.account.bank", label: "Bank account" },
                  {
                    k: "gl.account.accounts_receivable",
                    label: "Accounts Receivable",
                  },
                  { k: "gl.account.sales_revenue", label: "Sales Revenue" },
                  // Tax-payable mappings are only relevant when the Tax feature
                  // is ON. When OFF they are hidden (the underlying mapping rows
                  // are left untouched in app_settings — just unused).
                  ...(taxEnabled
                    ? [
                        { k: "gl.account.vat_payable", label: "VAT Payable" },
                        { k: "gl.account.nhil_payable", label: "NHIL Payable" },
                        {
                          k: "gl.account.getfund_payable",
                          label: "GETFund Payable",
                        },
                      ]
                    : []),
                ].map((row) => (
                  <AccountingMappingInput
                    key={row.k}
                    k={row.k}
                    label={row.label}
                    accounts={glAccounts}
                    settings={settings}
                    onChange={setSetting}
                  />
                ))}
                {canWrite ? (
                  <div className="md:col-span-2 flex justify-end">
                    <Button
                      onClick={() =>
                        saveKeys(
                          [
                            "gl.account.cash",
                            "gl.account.bank",
                            "gl.account.accounts_receivable",
                            "gl.account.sales_revenue",
                            ...(taxEnabled
                              ? [
                                  "gl.account.vat_payable",
                                  "gl.account.nhil_payable",
                                  "gl.account.getfund_payable",
                                ]
                              : []),
                          ],
                          "Accounting mappings",
                          setSavingAccounting,
                        )
                      }
                      className="bg-gradient-primary"
                      disabled={savingAccounting}
                    >
                      {savingAccounting ? "Saving..." : "Save mappings"}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}

        <TabsContent value="terms" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">
                Quotation Terms &amp; Conditions
              </CardTitle>
              <CardDescription>
                These terms appear at the bottom of every quotation PDF. Use{" "}
                <span className="font-mono text-foreground">
                  {"{company.name}"}
                </span>{" "}
                to substitute your company name.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                rows={10}
                value={
                  settings["quotation.terms"] !== undefined
                    ? settings["quotation.terms"]
                    : DEFAULT_QUOTATION_TERMS
                }
                onChange={(e) => setSetting("quotation.terms", e.target.value)}
                placeholder={DEFAULT_QUOTATION_TERMS}
                className="font-mono text-xs leading-relaxed whitespace-pre-wrap"
              />
              {canWrite ? (
                <div className="flex items-center justify-between gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      setSetting("quotation.terms", DEFAULT_QUOTATION_TERMS)
                    }
                    disabled={savingTerms}
                  >
                    Reset to default
                  </Button>
                  <Button
                    onClick={() =>
                      saveKeys(["quotation.terms"], "Terms", setSavingTerms)
                    }
                    className="bg-gradient-primary"
                    disabled={savingTerms}
                  >
                    {savingTerms ? "Saving..." : "Save terms"}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">
                Sales Invoice Terms &amp; Conditions
              </CardTitle>
              <CardDescription>
                These terms appear at the bottom of every sales invoice PDF.
                Use{" "}
                <span className="font-mono text-foreground">
                  {"{company.name}"}
                </span>{" "}
                to substitute your company name.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                rows={10}
                value={
                  settings["sales_invoice.terms"] !== undefined
                    ? settings["sales_invoice.terms"]
                    : DEFAULT_SALES_INVOICE_TERMS
                }
                onChange={(e) =>
                  setSetting("sales_invoice.terms", e.target.value)
                }
                placeholder={DEFAULT_SALES_INVOICE_TERMS}
                className="font-mono text-xs leading-relaxed whitespace-pre-wrap"
              />
              {canWrite ? (
                <div className="flex items-center justify-between gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      setSetting(
                        "sales_invoice.terms",
                        DEFAULT_SALES_INVOICE_TERMS,
                      )
                    }
                    disabled={savingInvoiceTerms}
                  >
                    Reset to default
                  </Button>
                  <Button
                    onClick={() =>
                      saveKeys(
                        ["sales_invoice.terms"],
                        "Invoice terms",
                        setSavingInvoiceTerms,
                      )
                    }
                    className="bg-gradient-primary"
                    disabled={savingInvoiceTerms}
                  >
                    {savingInvoiceTerms ? "Saving..." : "Save invoice terms"}
                  </Button>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin ? (
          <TabsContent value="users">
            <Card className="shadow-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-lg">User Accounts</CardTitle>
                  <CardDescription>
                    Invite team members, manage roles and per-user module access.
                  </CardDescription>
                </div>
                <Dialog open={userOpen} onOpenChange={setUserOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-primary">
                      <Plus className="h-4 w-4 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>New user</DialogTitle>
                      <DialogDescription>
                        Account credentials and initial role.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-3">
                      <div className="space-y-1.5">
                        <Label>Username *</Label>
                        <Input
                          type="text"
                          autoComplete="off"
                          placeholder="e.g. jdoe"
                          value={userForm.username}
                          onChange={(e) =>
                            setUserForm((f) => ({
                              ...f,
                              username: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          autoComplete="off"
                          value={userForm.email}
                          onChange={(e) =>
                            setUserForm((f) => ({ ...f, email: e.target.value }))
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Full name</Label>
                        <Input
                          value={userForm.name}
                          onChange={(e) =>
                            setUserForm((f) => ({ ...f, name: e.target.value }))
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Role</Label>
                        <Select
                          value={userForm.role || undefined}
                          onValueChange={clearable((v) =>
                            setUserForm((f) => ({ ...f, role: v as RoleKey }))
                          )}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectNoneItem />
                            {ALL_ROLES.map((r) => (
                              <SelectItem
                                key={r}
                                value={r}
                                className="capitalize"
                              >
                                {r}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Initial password *</Label>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          value={userForm.password}
                          onChange={(e) =>
                            setUserForm((f) => ({
                              ...f,
                              password: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setUserOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={addUser} className="bg-gradient-primary">
                        Create user
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right pr-6">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-muted-foreground py-10"
                        >
                          No users yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((u) => {
                        const isSelf = u.id === user?.id;
                        const role = u.roles?.[0] ?? "—";
                        return (
                          <TableRow key={u.id}>
                            <TableCell className="font-medium">
                              {u.username || "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {u.email}
                            </TableCell>
                            <TableCell>{u.name || "—"}</TableCell>
                            <TableCell>
                              <Select
                                value={u.roles?.[0] ?? undefined}
                                onValueChange={(v) =>
                                  changeRole(u, v as RoleKey)
                                }
                                disabled={isSelf || roleSavingId === u.id}
                              >
                                <SelectTrigger
                                  className="h-8 w-[140px] capitalize"
                                  title={
                                    isSelf
                                      ? "You cannot change your own role"
                                      : roleSavingId === u.id
                                        ? "Saving…"
                                        : "Change role"
                                  }
                                >
                                  <SelectValue placeholder={role}>
                                    {roleSavingId === u.id ? "Saving…" : role}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {ALL_ROLES.map((r) => (
                                    <SelectItem
                                      key={r}
                                      value={r}
                                      className="capitalize"
                                    >
                                      {r}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  u.isActive
                                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                    : "border-muted-foreground/30 bg-muted text-muted-foreground"
                                }
                              >
                                {u.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right pr-3">
                              <div className="inline-flex items-center gap-1 justify-end">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  title={
                                    isSelf
                                      ? "You cannot deactivate your own account"
                                      : u.isActive
                                        ? "Deactivate user"
                                        : "Activate user"
                                  }
                                  disabled={isSelf}
                                  onClick={() => toggleActive(u)}
                                >
                                  {u.isActive ? (
                                    <PowerOff
                                      className={
                                        isSelf
                                          ? "h-4 w-4 text-muted-foreground"
                                          : "h-4 w-4 text-emerald-600 dark:text-emerald-400"
                                      }
                                    />
                                  ) : (
                                    <Power
                                      className={
                                        isSelf
                                          ? "h-4 w-4 text-muted-foreground"
                                          : "h-4 w-4 text-muted-foreground"
                                      }
                                    />
                                  )}
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  title="Edit access"
                                  onClick={() => openAccess(u)}
                                >
                                  <Shield className="h-4 w-4 text-primary" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  title="Reset password"
                                  onClick={() => openPwReset(u)}
                                >
                                  <KeyRound className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  title={
                                    isSelf
                                      ? "You cannot delete your own account"
                                      : "Delete user"
                                  }
                                  disabled={isSelf}
                                  onClick={() => deleteUser(u)}
                                >
                                  <Trash2
                                    className={
                                      isSelf
                                        ? "h-4 w-4 text-muted-foreground"
                                        : "h-4 w-4 text-destructive"
                                    }
                                  />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        ) : null}

        <TabsContent value="billing" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="h-5 w-5" />
                Company access
              </CardTitle>
              <CardDescription>
                Lock the app when payment is pending. Only administrators can
                sign in while locked. After payment is confirmed, unlock and set
                a paid-until date; access ends automatically after that date
                unless you extend it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {billingLoading && !billingStatus ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : billingStatus ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">Status</span>
                  {billingStatus.locked ? (
                    <Badge variant="destructive">Locked</Badge>
                  ) : (
                    <Badge variant="secondary">Active</Badge>
                  )}
                  {billingStatus.reason === "manual" ? (
                    <span className="text-sm text-muted-foreground">
                      (manual lock)
                    </span>
                  ) : billingStatus.reason === "expired" ? (
                    <span className="text-sm text-muted-foreground">
                      (paid-until date passed)
                    </span>
                  ) : null}
                  {billingStatus.paid_until ? (
                    <span className="text-sm text-muted-foreground">
                      Paid until{" "}
                      <span className="font-mono text-foreground">
                        {billingStatus.paid_until}
                      </span>
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      No paid-until date set yet
                    </span>
                  )}
                </div>
              ) : null}

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-3 rounded-lg border p-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Lock app
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Non-administrators cannot log in and active sessions are
                    ended immediately.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="billing-lock-note">Note (optional)</Label>
                    <Textarea
                      id="billing-lock-note"
                      rows={2}
                      placeholder="e.g. Awaiting bank transfer"
                      value={billingLockNote}
                      onChange={(e) => setBillingLockNote(e.target.value)}
                    />
                  </div>
                  <Button
                    variant="destructive"
                    disabled={
                      billingBusy !== null ||
                      billingLoading ||
                      !!billingStatus?.locked
                    }
                    onClick={() => void lockCompanyAccess()}
                  >
                    {billingBusy === "lock" ? "Locking…" : "Lock app"}
                  </Button>
                </div>

                <div className="space-y-3 rounded-lg border p-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Unlock className="h-4 w-4" />
                    Unlock app
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Confirm payment and set how long access stays open (today or
                    a future date).
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="billing-paid-until">Paid until</Label>
                    <Input
                      id="billing-paid-until"
                      type="date"
                      value={billingUnlockDate}
                      onChange={(e) => setBillingUnlockDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billing-unlock-note">Note (optional)</Label>
                    <Textarea
                      id="billing-unlock-note"
                      rows={2}
                      placeholder="e.g. Invoice #123 paid"
                      value={billingUnlockNote}
                      onChange={(e) => setBillingUnlockNote(e.target.value)}
                    />
                  </div>
                  <Button
                    disabled={
                      billingBusy !== null ||
                      billingLoading ||
                      !billingUnlockDate
                    }
                    onClick={() => void unlockCompanyAccess()}
                  >
                    {billingBusy === "unlock" ? "Unlocking…" : "Unlock app"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="text-lg">Access history</CardTitle>
              <CardDescription>
                Record of lock and unlock actions by administrators.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Paid until</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingHistory.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-muted-foreground"
                      >
                        No history yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    billingHistory.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {new Date(row.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              row.action === "lock" ? "destructive" : "secondary"
                            }
                          >
                            {row.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {row.paid_until ?? "—"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.actor_name ?? "—"}
                        </TableCell>
                        <TableCell className="text-sm max-w-[240px] truncate">
                          {row.note ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="server">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Server className="h-5 w-5" />
                Cloud API
              </CardTitle>
              <CardDescription>
                The web app talks to the BillForge Worker at{" "}
                <span className="font-mono text-foreground">
                  {import.meta.env.VITE_API_BASE_URL ??
                    "http://localhost:8787/api"}
                </span>
                . Configure it via the{" "}
                <span className="font-mono text-foreground">
                  VITE_API_BASE_URL
                </span>{" "}
                env var in <span className="font-mono">web/.env</span>.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>
                Authentication is handled by the API. Use{" "}
                <span className="font-mono text-foreground">
                  POST /api/auth/bootstrap
                </span>{" "}
                once to create the first administrator.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Module Access dialog */}
      <Dialog open={accessOpen} onOpenChange={handleAccessOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Module Access
              {accessTarget ? (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  — {accessTarget.username || accessTarget.email}
                </span>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Tick any module to grant it. Supporting reads (Customers,
              Inventory) are added automatically. Untick anything to deny it;
              denied access stays denied.
            </DialogDescription>
            {accessTarget?.id === user?.id ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                You cannot remove your own Settings access.
              </p>
            ) : null}
          </DialogHeader>
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  {ACTION_COLUMNS.map((a) => (
                    <TableHead key={a} className="text-center capitalize w-24">
                      {a}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {accessLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={1 + ACTION_COLUMNS.length}
                      className="text-center text-muted-foreground py-8"
                    >
                      Loading access…
                    </TableCell>
                  </TableRow>
                ) : (
                  MODULES.map((m) => (
                    <TableRow key={m.key}>
                      <TableCell className="font-medium">{m.label}</TableCell>
                      {ACTION_COLUMNS.map((a) => {
                        const flat = modulePermission(m.key, a);
                        if (!flat) {
                          return (
                            <TableCell
                              key={a}
                              className="text-center text-muted-foreground/50"
                            >
                              —
                            </TableCell>
                          );
                        }
                        const checked = accessMatrix[m.key]?.[a] === true;
                        return (
                          <TableCell key={a} className="text-center">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleCell(m.key, a)}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {pendingMissing.length > 0 ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200 px-4 py-3 space-y-2">
              <p className="font-medium">
                Heads up — some forms will return Forbidden for{" "}
                {accessTarget?.name ||
                  accessTarget?.username ||
                  accessTarget?.email ||
                  "this user"}
                .
              </p>
              <p className="text-sm">
                The actions you ticked rely on reads you didn’t grant. Without
                these, the user will hit a Forbidden error when opening the
                form. Tick the missing reads to enable them, or save anyway
                and accept the lock-out.
              </p>
              <ul className="text-sm list-disc pl-5 space-y-1">
                {pendingMissing.map((m) => (
                  <li key={m.flat}>
                    <span className="font-medium">{m.moduleLabel}</span>{" "}
                    (
                    <span className="capitalize">{m.actionKey}</span>) — needed
                    by{" "}
                    {m.neededBy
                      .map((n) => `${n.moduleLabel} (${n.actionKey})`)
                      .join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <DialogFooter className="sm:justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetAccessToRoleDefault}
              disabled={
                accessSaving ||
                accessLoading ||
                accessResetting ||
                !accessTarget
              }
              className="text-muted-foreground hover:text-destructive"
              title="Clear all per-user overrides and fall back to the role defaults"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {accessResetting ? "Resetting…" : "Reset to role default"}
            </Button>
            <div className="flex items-center gap-2">
              {pendingMissing.length > 0 ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setPendingMissing([])}
                    disabled={accessSaving}
                  >
                    Cancel and edit
                  </Button>
                  <Button
                    onClick={commitAccess}
                    disabled={accessSaving}
                    variant="destructive"
                  >
                    {accessSaving ? "Saving…" : "Save anyway"}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleAccessOpenChange(false)}
                    disabled={accessSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveAccess}
                    disabled={accessSaving || accessLoading}
                    className="bg-gradient-primary"
                  >
                    {accessSaving ? "Saving…" : "Save Access"}
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              {pwTarget
                ? `Set a new password for ${pwTarget.username || pwTarget.email}.`
                : "Set a new password."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>New password *</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={pwValue}
                onChange={(e) => setPwValue(e.target.value)}
              />
            </div>
            <PasswordRules value={pwValue} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitPwReset}
              className="bg-gradient-primary"
              disabled={!validatePassword(pwValue).ok}
            >
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground">{label}</Label>
      <div className="text-sm font-medium text-foreground break-all">
        {value}
      </div>
    </div>
  );
}

function PasswordField({
  id,
  label,
  value,
  show,
  onShowChange,
  onChange,
  autoComplete,
  error,
}: {
  id: string;
  label: string;
  value: string;
  show: boolean;
  onShowChange: (v: boolean) => void;
  onChange: (v: string) => void;
  autoComplete?: string;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
        />
        <button
          type="button"
          aria-label={show ? "Hide password" : "Show password"}
          onClick={() => onShowChange(!show)}
          className="absolute inset-y-0 right-0 flex items-center justify-center px-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          {show ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : null}
    </div>
  );
}

function PasswordRules({ value }: { value: string }) {
  const rules = passwordRules(value);
  return (
    <ul className="rounded-md border border-border bg-muted/30 dark:bg-muted/20 px-3 py-2 space-y-1 text-xs">
      {rules.map((r) => (
        <li
          key={r.key}
          className={
            r.ok
              ? "flex items-center gap-2 text-emerald-700 dark:text-emerald-400"
              : "flex items-center gap-2 text-muted-foreground"
          }
        >
          {r.ok ? (
            <Check className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <X className="h-3.5 w-3.5 shrink-0" />
          )}
          <span>{r.label}</span>
        </li>
      ))}
    </ul>
  );
}

function FeatureToggle({
  label,
  description,
  enabled,
  disabled,
  onToggle,
}: {
  label: string;
  description: string;
  enabled: boolean;
  disabled?: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
      <div className="space-y-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <Badge
            variant="outline"
            className={
              enabled
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "border-muted-foreground/30 bg-muted text-muted-foreground"
            }
          >
            {enabled ? "On" : "Off"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Button
        type="button"
        variant={enabled ? "outline" : "default"}
        className={enabled ? "" : "bg-gradient-primary"}
        disabled={disabled}
        onClick={() => onToggle(!enabled)}
      >
        {enabled ? (
          <>
            <PowerOff className="h-4 w-4 mr-2" />
            Turn off
          </>
        ) : (
          <>
            <Power className="h-4 w-4 mr-2" />
            Turn on
          </>
        )}
      </Button>
    </div>
  );
}

function SettingInput({
  k,
  label,
  type,
  settings,
  onChange,
}: {
  k: string;
  label: string;
  type?: string;
  settings: Record<string, string>;
  onChange: (k: string, v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input
        type={type ?? "text"}
        value={settings[k] ?? ""}
        onChange={(e) => onChange(k, e.target.value)}
      />
    </div>
  );
}

function AccountingMappingInput({
  k,
  label,
  accounts,
  settings,
  onChange,
}: {
  k: string;
  label: string;
  accounts: Array<{ id: number; code: string; name: string; type: string }>;
  settings: Record<string, string>;
  onChange: (k: string, v: string) => void;
}) {
  const current = settings[k] ?? "";
  const match = accounts.find((a) => a.code === current);
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <Select
          value={current || undefined}
          onValueChange={clearable((v) => onChange(k, v))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pick account" />
          </SelectTrigger>
          <SelectContent>
            <SelectNoneItem />
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.code}>
                <span className="font-mono mr-2">{a.code}</span>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="text-xs text-muted-foreground">
        {match ? `${match.name} (${match.type})` : current ? `Code: ${current}` : "Not set"}
      </div>
    </div>
  );
}
